import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import type { SocialContactNetwork } from '@/components/ui/social-contact-link'
import { normalizeWebsiteUrl } from '@/lib/normalize-website-url'

const X_CONTACT_FIELD_LABEL = 'X (Twitter)'

type ContactFieldKind = 'phone' | 'website' | SocialContactNetwork

const CONTACT_FIELD_META: Record<
  ContactFieldKind,
  { label: string; idPrefix: string; placeholder?: string }
> = {
  phone: { label: 'Phone', idPrefix: 'phone' },
  website: { label: 'Website', idPrefix: 'website', placeholder: 'example.com' },
  linkedin: {
    label: 'LinkedIn',
    idPrefix: 'linkedin',
    placeholder: 'linkedin.com/in/...',
  },
  x: {
    label: X_CONTACT_FIELD_LABEL,
    idPrefix: 'x',
    placeholder: '@handle or x.com/handle',
  },
  instagram: {
    label: 'Instagram',
    idPrefix: 'instagram',
    placeholder: '@handle or instagram.com/handle',
  },
  facebook: {
    label: 'Facebook',
    idPrefix: 'facebook',
    placeholder: 'facebook.com/page',
  },
  threads: {
    label: 'Threads',
    idPrefix: 'threads',
    placeholder: '@handle or threads.net/@handle',
  },
  tiktok: {
    label: 'TikTok',
    idPrefix: 'tiktok',
    placeholder: '@handle or tiktok.com/@handle',
  },
  youtube: {
    label: 'YouTube',
    idPrefix: 'youtube',
    placeholder: '@handle or youtube.com/@handle',
  },
  substack: {
    label: 'Substack',
    idPrefix: 'substack',
    placeholder: 'handle.substack.com',
  },
  github: {
    label: 'GitHub',
    idPrefix: 'github',
    placeholder: 'github.com/handle',
  },
}

type SocialContactFieldProps = {
  network: SocialContactNetwork
  value: string
  onChange: (value: string) => void
  id?: string
  disabled?: boolean
}

export function SocialContactField({
  network,
  value,
  onChange,
  id,
  disabled = false,
}: SocialContactFieldProps) {
  const meta = CONTACT_FIELD_META[network]
  const fieldId = id ?? `social-contact-${meta.idPrefix}`

  return (
    <FormField label={meta.label} htmlFor={fieldId}>
      <Input
        id={fieldId}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={meta.placeholder}
        autoComplete="off"
        disabled={disabled}
      />
    </FormField>
  )
}

export function LinkedInContactField(
  props: Omit<SocialContactFieldProps, 'network'>,
) {
  return <SocialContactField network="linkedin" {...props} />
}

export function XContactField(props: Omit<SocialContactFieldProps, 'network'>) {
  return <SocialContactField network="x" {...props} />
}

type PhoneContactFieldProps = {
  value: string
  onChange: (value: string) => void
  id?: string
  disabled?: boolean
}

export function PhoneContactField({
  value,
  onChange,
  id,
  disabled = false,
}: PhoneContactFieldProps) {
  const fieldId = id ?? 'phone-contact'
  const meta = CONTACT_FIELD_META.phone

  return (
    <FormField label={meta.label} htmlFor={fieldId}>
      <Input
        id={fieldId}
        type="tel"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="+61 412 345 678 or 0412 345 678"
        autoComplete="tel"
        disabled={disabled}
      />
    </FormField>
  )
}

type WebsiteContactFieldProps = {
  value: string
  onChange: (value: string) => void
  id?: string
  disabled?: boolean
}

export function WebsiteContactField({
  value,
  onChange,
  id,
  disabled = false,
}: WebsiteContactFieldProps) {
  const fieldId = id ?? 'website-contact'
  const meta = CONTACT_FIELD_META.website

  return (
    <FormField label={meta.label} htmlFor={fieldId}>
      <Input
        id={fieldId}
        type="url"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => onChange(normalizeWebsiteUrl(event.target.value))}
        placeholder={meta.placeholder}
        autoComplete="url"
        disabled={disabled}
      />
    </FormField>
  )
}
